/**
 * RoleGuard Component
 * Task: 32.1 - Auth Foundation Components
 *
 * Route-group level authorization guard that checks:
 * - User types (learner/employer/platform)
 * - Employer primary representative status
 * - RBAC permissions
 *
 * Redirects to fallback route if authorization fails.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { hasPermission } from '@/lib/authorization-utils';
import { useCurrentUser, type UserType } from '@/lib/auth';
import { useEmployerProfile } from '@/hooks/use-employer-profile';
import { RouteLoader } from '@/components/ui/feedback/route-loader';
import { ROUTES } from '@/config/routes';
import type { PermissionName } from '@/types/rbac';

interface RoleGuardProps {
  allowedTypes: UserType[];          // REQUIRED - which user types can access
  requirePrimary?: boolean;          // Employer: requires is_primary_representative
  requiredPermission?: PermissionName; // Platform: requires RBAC permission
  requiredPermissions?: PermissionName[]; // Platform: requires any of these
  fallback?: string;                 // Redirect route on failure (default: '/dashboard')
  children?: React.ReactNode;
}

/**
 * RoleGuard - Route-level authorization boundary
 *
 * Usage:
 * ```tsx
 * // Learner routes
 * <Route path="/learner" element={<RoleGuard allowedTypes={['learner']} />}>
 *   <Route path="dashboard" element={<LearnerDashboard />} />
 * </Route>
 *
 * // Employer team management (primary rep only)
 * <Route path="/employer/team" element={<RoleGuard allowedTypes={['employer']} requirePrimary />}>
 *   <Route path="" element={<TeamManagement />} />
 * </Route>
 *
 * // Admin role management (permission-gated)
 * <Route path="/admin/roles" element={<RoleGuard allowedTypes={['platform']} requiredPermission="admin:manage" />}>
 *   <Route path="" element={<RoleManagement />} />
 * </Route>
 * ```
 */
export function RoleGuard({
  allowedTypes,
  requirePrimary = false,
  requiredPermission,
  requiredPermissions,
  fallback = '/dashboard',
  children,
}: RoleGuardProps) {
  const location = useLocation();
  const { auth, userTypes, isLoading: typesLoading, isError: typesError } = useCurrentUser();
  const { data: employerProfile, isLoading: employerLoading, error: employerError } = useEmployerProfile();

  // Check for errors
  const error = typesError || (requirePrimary && employerError);

  if (error) {
    console.error('[RoleGuard] Error loading authorization data:', error);
    return <Navigate to={fallback} replace />;
  }

  // Check loading states
  const isLoading = typesLoading || (requirePrimary && employerLoading);

  if (isLoading) {
    return <RouteLoader variant="minimal" />;
  }

  // Check user type (empty array means user hasn't completed onboarding)
  if (!userTypes || userTypes.length === 0) {
    return <Navigate to={ROUTES.GET_STARTED} replace />;
  }

  // Check if user has any of the allowed types
  const hasAllowedType = allowedTypes.some(type => userTypes.includes(type));
  if (!hasAllowedType) {
    return <Navigate to={fallback} state={{ from: location }} replace />;
  }

  // Check employer primary status
  if (requirePrimary && !employerProfile?.isPrimary) {
    return <Navigate to={ROUTES.EMPLOYER_DASHBOARD} replace />;
  }

  // Check single permission (synchronous check using utility)
  if (requiredPermission && auth?.permissions) {
    const allowed = hasPermission(auth.permissions, requiredPermission);
    if (!allowed) {
      return <Navigate to={fallback} replace />;
    }
  }

  // Check any of multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0 && auth?.permissions) {
    const hasAny = requiredPermissions.some(perm =>
      hasPermission(auth.permissions, perm)
    );
    if (!hasAny) {
      return <Navigate to={fallback} replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
}
